import { YARD, isBranch, isHome } from "./types.js";
import { legalMoves, legalSequences } from "./moves.js";
import { progress, walk } from "./path.js";
import { SAFE } from "./types.js";
/** Returns a legal move only; difficulty changes priorities, never permissions. */
import { isPlayableSeat } from "./validation.js";
export function chooseBotMove(state, random = Math.random) {
    // Defensive: if the current seat isn't playable, do not select a move.
    if (!isPlayableSeat(state, state.turn))
        return null;
    // If there are two or more dice available, evaluate full two-dice sequences.
    const dice = state.dice ?? (state.die ? [state.die] : []);
    const difficulty = state.players.find((candidate) => candidate.seat === state.turn)?.bot ?? "medium";
    if (dice && dice.length >= 2) {
        // Use sequence enumeration (legalSequences imported at top-level)
        let sequences = legalSequences(state);
        if (!sequences.length)
            return null;
        // Authoritative reducer expects the first die to be state.die (dice[0]). Only consider
        // sequences that start with the currently active die to avoid returning moves that
        // are illegal when applied against state.die (this was causing applyMove illegal_move errors).
        const currentDie = state.die ?? (state.dice ? state.dice[0] : undefined);
        if (typeof currentDie === 'number') {
            const filtered = sequences.filter((s) => s.order && s.order.length > 0 && s.order[0] === currentDie);
            if (filtered.length > 0)
                sequences = filtered;
            // If no sequences match the current die, fall back to single-die evaluation below
            // to avoid returning an illegal first move.
            else
                return null;
        }
        if (difficulty === "easy") {
            const seq = sequences[Math.floor(random() * sequences.length)];
            return seq && seq.moves && seq.moves.length ? seq.moves[0] : null;
        }
        const rankSeq = (seq) => {
            if (difficulty === "medium")
                return scoreSequenceMedium(state, seq.moves);
            if (difficulty === "hard")
                return scoreSequenceHard(state, seq.moves);
            return scoreSequenceExpert(state, seq.moves);
        };
        const best = sequences.map((s) => ({ s, score: rankSeq(s) })).sort((a, b) => b.score - a.score)[0];
        if (!best || !best.s || !best.s.moves || !best.s.moves.length)
            return null;
        const candidate = best.s.moves[0];
        // Ensure the selected first move is legal for the current active die (authoritative).
        const firstDie = state.die ?? (state.dice ? state.dice[0] : undefined);
        const legalFirst = (firstDie ? legalMoves(state, firstDie) : legalMoves(state)).find((m) => m.tokenId === candidate.tokenId && m.from === candidate.from && m.to === candidate.to);
        if (legalFirst)
            return legalFirst;
        // If the candidate isn't legal for the current die, decline to select a sequence here and
        // fall back to single-die evaluation to avoid illegal_move exceptions.
        return null;
    }
    // Fallback to single-die evaluation for compatibility with existing tests and states
    const moves = legalMoves(state);
    if (!moves.length)
        return null;
    if (difficulty === "easy")
        return moves[Math.floor(random() * moves.length)] ?? moves[0] ?? null;
    const ranked = [...moves].sort((first, second) => {
        const score = difficulty === "medium"
            ? scoreMedium(state, second) - scoreMedium(state, first)
            : difficulty === "hard"
                ? scoreHard(state, second) - scoreHard(state, first)
                : scoreExpert(state, second) - scoreExpert(state, first);
        return score || first.tokenId.localeCompare(second.tokenId);
    });
    return ranked[0] ?? null;
}
/** Sequence scoring: sum of per-move scores applied to successive preview states. */
function scoreSequenceMedium(state, moves) {
    let s = state;
    let total = 0;
    for (const m of moves) {
        total += scoreMedium(s, m);
        s = preview(s, m);
    }
    return total;
}
function scoreSequenceHard(state, moves) {
    let s = state;
    let total = 0;
    for (const m of moves) {
        total += scoreHard(s, m);
        s = preview(s, m);
    }
    return total;
}
function scoreSequenceExpert(state, moves) {
    let s = state;
    let total = 0;
    for (const m of moves) {
        total += scoreExpert(s, m);
        s = preview(s, m);
    }
    return total;
}
function preview(state, move) {
    return {
        ...state,
        tokens: state.tokens.map((token) => token.id === move.tokenId ? { ...token, cell: move.to } : move.captures.includes(token.id) ? { ...token, cell: YARD } : token),
    };
}
/** Medium bot: captures, exits and safe landings before raw distance. */
export function scoreMedium(state, move) {
    const token = findToken(state, move);
    const gain = token ? progress(token.color, move.to) - progress(token.color, move.from) : 0;
    return 1200 * Number(move.captures.length > 0)
        + 480 * Number(move.exitsYard)
        + 420 * Number(move.finishes)
        + 200 * Number(move.entersBranch)
        + 130 * Number(move.formsBlock)
        + 115 * Number(SAFE.has(move.to))
        + gain;
}
/** Hard bot adds an original, deterministic danger estimate after every move. */
export function scoreHard(state, move) {
    const token = findToken(state, move);
    if (!token)
        return Number.NEGATIVE_INFINITY;
    const after = previewMove(state, move);
    const danger = isVulnerable(after, token.color, move.to) ? 1 : 0;
    const safetyExit = !SAFE.has(token.cell) && SAFE.has(move.to);
    return scoreMedium(state, move) + 240 * Number(safetyExit) - 530 * danger + 55 * expectedMobility(after);
}
/** Expert bot searches one future dice layer for its best likely continuation. */
export function scoreExpert(state, move) {
    const after = previewMove(state, move);
    const future = [1, 2, 3, 4, 5, 6].reduce((total, die) => {
        const options = legalMoves(after, die);
        const best = options.reduce((score, option) => Math.max(score, scoreHard(after, option)), 0);
        return total + best;
    }, 0) / 6;
    return scoreHard(state, move) + future * 0.18;
}
function findToken(state, move) { return state.tokens.find((candidate) => candidate.id === move.tokenId); }
function previewMove(state, move) {
    return {
        ...state,
        tokens: state.tokens.map((token) => token.id === move.tokenId ? { ...token, cell: move.to } : move.captures.includes(token.id) ? { ...token, cell: YARD } : token),
    };
}
function isVulnerable(state, color, cell) {
    if (SAFE.has(cell) || isBranch(cell) || isHome(cell))
        return false;
    return state.tokens.some((enemy) => {
        if (enemy.color === color || enemy.cell === YARD || isBranch(enemy.cell) || isHome(enemy.cell))
            return false;
        return [1, 2, 3, 4, 5, 6].some((die) => walk(enemy.color, enemy.cell, die)?.at(-1) === cell);
    });
}
function expectedMobility(state) {
    return [1, 2, 3, 4, 5, 6].reduce((total, die) => total + legalMoves(state, die).length, 0);
}
